/**
 * WorkspaceMembersList Component
 *
 * Displays list of workspace members with roles and management actions.
 * 
 * ACCESS MODEL (simplified):
 * - Workspace membership grants access to ALL properties attached to that workspace
 * - No per-user property access display needed
 */

import { useState } from "react"
import { SettingsList, SettingsListRow } from '@/components/shared/settings/SettingsKit'
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { TooltipProvider } from "@/components/ui/tooltip"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { WorkspaceMember, WorkspaceRole } from "@/lib/types/workspace-team"

interface WorkspaceMembersListProps {
  members: WorkspaceMember[]
  isOwner: boolean
  currentUserEmail?: string
  onRemoveMember: (userId: string) => Promise<void>
  onUpdateRole?: (userId: string, role: WorkspaceRole) => Promise<void>
  isLoading?: boolean
}

export default function WorkspaceMembersList({
  members,
  isOwner,
  currentUserEmail,
  onRemoveMember,
  onUpdateRole,
  isLoading = false,
}: WorkspaceMembersListProps) {
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<WorkspaceMember | null>(null)
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null)

  const handleRemove = async () => {
    if (!memberToRemove) return

    setRemovingMemberId(memberToRemove.user_id)
    try {
      await onRemoveMember(memberToRemove.user_id)
    } finally {
      setRemovingMemberId(null)
      setMemberToRemove(null)
    }
  }

  const handleRoleChange = async (member: WorkspaceMember, newRole: WorkspaceRole) => {
    if (!onUpdateRole || member.role === newRole) return

    setUpdatingRoleId(member.user_id)
    try {
      await onUpdateRole(member.user_id, newRole)
    } finally {
      setUpdatingRoleId(null)
    }
  }

  const getRoleBadge = (member: WorkspaceMember) => {
    const label =
      member.is_owner || member.role === 'owner'
        ? 'Owner'
        : member.role === 'admin'
          ? 'Admin'
          : member.role === 'member'
            ? 'Member'
            : 'Viewer'
    return <span className="px-2 text-xs font-medium leading-4 text-foreground/60">{label}</span>
  }

  const isCurrentUser = (member: WorkspaceMember) =>
    currentUserEmail &&
    member.email.toLowerCase() === currentUserEmail.toLowerCase()

  const canEditRole = (member: WorkspaceMember) => {
    // Only owners can edit roles
    if (!isOwner) return false
    // Cannot change owner's role
    if (member.is_owner || member.role === 'owner') return false
    // Cannot demote yourself
    if (isCurrentUser(member)) return false
    return true
  }

  if (members.length === 0) {
    return null
  }

  return (
    <TooltipProvider>
      <SettingsList>
        {members.map((member) => (
          <SettingsListRow
            key={member.user_id}
            leading={
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-canvas text-[11px] font-semibold text-foreground">
                {(member.name || member.email).charAt(0).toUpperCase()}
              </span>
            }
            primary={
              <>
                {member.name || member.email}
                {isCurrentUser(member) && <span className="ml-1.5 font-normal text-foreground/60">(you)</span>}
              </>
            }
            secondary={member.name ? member.email : undefined}
            trailing={
              <>
                {canEditRole(member) && onUpdateRole ? (
                  <Select
                    value={member.role}
                    onValueChange={(value) => handleRoleChange(member, value as WorkspaceRole)}
                    disabled={isLoading || updatingRoleId === member.user_id}
                  >
                    <SelectTrigger
                      aria-label={`Role for ${member.email}`}
                      className="h-7 min-w-[92px] rounded-md bg-background px-2 text-xs data-[size=default]:h-7"
                    >
                      <SelectValue>
                        {updatingRoleId === member.user_id
                          ? 'Updating…'
                          : member.role === 'admin'
                            ? 'Admin'
                            : member.role === 'member'
                              ? 'Member'
                              : 'Viewer'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  getRoleBadge(member)
                )}
                {isOwner && !member.is_owner && member.role !== 'owner' && !isCurrentUser(member) && (
                  <button
                    type="button"
                    onClick={() => setMemberToRemove(member)}
                    disabled={isLoading || removingMemberId === member.user_id}
                    className="h-7 rounded-md px-2 text-xs font-medium text-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {removingMemberId === member.user_id ? 'Removing…' : 'Remove'}
                  </button>
                )}
              </>
            }
          />
        ))}
      </SettingsList>

      {/* Remove confirmation dialog */}
      <Dialog
        open={!!memberToRemove}
        onOpenChange={(open) => {
          if (!removingMemberId && !open) setMemberToRemove(null)
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-base font-medium">Remove team member?</DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground leading-relaxed">
              Remove{' '}
              <span className="font-medium text-foreground">{memberToRemove?.email}</span>? They
              will lose access to all sites in this workspace.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setMemberToRemove(null)}
              disabled={!!removingMemberId}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={!!removingMemberId}
            >
              {removingMemberId ? "Removing..." : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  )
}
